import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  History as HistoryIcon,
  Description as TemplateIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Sms as SmsIcon
} from '@mui/icons-material';
import api from '../api/api';

interface Student {
  id: number;
  name: string;
  class_level: string;
  parent_email: string;
  parent_phone: string;
  student_id: string;
}

interface Staff {
  id: number;
  name: string;
  email: string;
  role: string;
  username: string;
}

interface Class {
  class_level: string;
  student_count: number;
  students: Student[];
}

interface MessageTemplate {
  id: number;
  name: string;
  subject: string;
  content: string;
  type: string;
  category: string;
  created_at: string;
  updated_at: string;
}

interface CommunicationHistory {
  id: number;
  type: string;
  recipient: string;
  subject: string;
  content: string;
  status: string;
  sent_at: string;
  error_message?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`communications-tabpanel-${index}`}
      aria-labelledby={`communications-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SchoolCommunications: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Send Message Tab
  const [targetType, setTargetType] = useState<'students' | 'parents' | 'staff' | 'class'>('parents');
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<number[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [enableEmail, setEnableEmail] = useState(true);
  const [enableWhatsApp, setEnableWhatsApp] = useState(false);
  const [enableSMS, setEnableSMS] = useState(false);
  const [showBulkWarning, setShowBulkWarning] = useState(false);

  // Data
  const [students, setStudents] = useState<Student[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [history, setHistory] = useState<CommunicationHistory[]>([]);

  // Templates Tab
  const [templateName, setTemplateName] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [templateType, setTemplateType] = useState('email');
  const [templateCategory, setTemplateCategory] = useState('general');
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);

  // History Tab
  const [selectedHistory, setSelectedHistory] = useState<CommunicationHistory | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Check for WhatsApp warning when bulk messaging parents
    if (targetType === 'parents' && enableWhatsApp) {
      const hasPhoneNumbers = students.some(student => student.parent_phone);
      setShowBulkWarning(!hasPhoneNumbers);
    } else {
      setShowBulkWarning(false);
    }
  }, [targetType, enableWhatsApp, students]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [studentsRes, staffRes, classesRes, templatesRes, historyRes] = await Promise.all([
        api.get('/communications/school/students'),
        api.get('/communications/school/staff'),
        api.get('/communications/school/classes'),
        api.get('/communications/school/templates'),
        api.get('/communications/school/history')
      ]);

      setStudents(studentsRes.data as any[]);
      setStaff(staffRes.data as any[]);
      setClasses(classesRes.data as any[]);
      setTemplates(templatesRes.data as any[]);
      setHistory(historyRes.data as any[]);
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({ type: 'error', text: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!content.trim()) {
      setMessage({ type: 'error', text: 'Message content is required' });
      return;
    }

    if (!enableEmail && !enableWhatsApp && !enableSMS) {
      setMessage({ type: 'error', text: 'At least one communication method must be enabled' });
      return;
    }

    setLoading(true);
    try {
      const requestData: any = {
        type: 'email',
        target_type: targetType,
        subject,
        content,
        enable_email: enableEmail,
        enable_whatsapp: enableWhatsApp,
        enable_sms: enableSMS
      };

      if (targetType === 'students') {
        requestData.target_ids = selectedStudents;
      } else if (targetType === 'staff') {
        requestData.target_ids = selectedStaff;
      } else if (targetType === 'class') {
        requestData.class_level = selectedClass;
      }

      const response = await api.post('/communications/school/send', requestData);
      
      if ((response.data as any).success) {
        setMessage({ type: 'success', text: (response.data as any).message });
        // Reset form
        setSubject('');
        setContent('');
        setSelectedStudents([]);
        setSelectedStaff([]);
        setSelectedClass('');
        // Reload history
        loadData();
      } else {
        setMessage({ type: 'error', text: 'Failed to send some messages' });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessage({ type: 'error', text: 'Failed to send message' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateName.trim() || !templateContent.trim()) {
      setMessage({ type: 'error', text: 'Template name and content are required' });
      return;
    }

    setLoading(true);
    try {
      const templateData = {
        name: templateName,
        subject: templateSubject,
        content: templateContent,
        type: templateType,
        category: templateCategory
      };

      await api.post('/communications/school/templates', templateData);
      setMessage({ type: 'success', text: 'Template created successfully' });
      
      // Reset form
      setTemplateName('');
      setTemplateSubject('');
      setTemplateContent('');
      setTemplateType('email');
      setTemplateCategory('general');
      
      // Reload templates
      loadData();
    } catch (error) {
      console.error('Error creating template:', error);
      setMessage({ type: 'error', text: 'Failed to create template' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;

    setLoading(true);
    try {
      const templateData = {
        name: templateName,
        subject: templateSubject,
        content: templateContent,
        type: templateType,
        category: templateCategory
      };

      await api.put(`/communications/school/templates/${editingTemplate.id}`, templateData);
      setMessage({ type: 'success', text: 'Template updated successfully' });
      
      // Reset form
      setEditingTemplate(null);
      setTemplateName('');
      setTemplateSubject('');
      setTemplateContent('');
      setTemplateType('email');
      setTemplateCategory('general');
      
      // Reload templates
      loadData();
    } catch (error) {
      console.error('Error updating template:', error);
      setMessage({ type: 'error', text: 'Failed to update template' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    setLoading(true);
    try {
      await api.delete(`/communications/school/templates/${templateId}`);
      setMessage({ type: 'success', text: 'Template deleted successfully' });
      loadData();
    } catch (error) {
      console.error('Error deleting template:', error);
      setMessage({ type: 'error', text: 'Failed to delete template' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (template: MessageTemplate) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateSubject(template.subject);
    setTemplateContent(template.content);
    setTemplateType(template.type);
    setTemplateCategory(template.category);
  };

  const handleUseTemplate = (template: MessageTemplate) => {
    setSubject(template.subject);
    setContent(template.content);
    setTabValue(0); // Switch to send message tab
  };

  const handleStudentSelection = (studentId: number) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleStaffSelection = (staffId: number) => {
    setSelectedStaff(prev => 
      prev.includes(staffId) 
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  const getRecipientCount = () => {
    switch (targetType) {
      case 'students':
        return selectedStudents.length;
      case 'parents':
        return students.length;
      case 'staff':
        return selectedStaff.length;
      case 'class':
        return classes.find(c => c.class_level === selectedClass)?.student_count || 0;
      default:
        return 0;
    }
  };

  const getStatusIcon = (status: string) => {
    return status === 'sent' ? <CheckCircleIcon color="success" /> : <ErrorIcon color="error" />;
  };

  const getStatusColor = (status: string) => {
    return status === 'sent' ? 'success' : 'error';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        School Communications
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Send Message" icon={<SendIcon />} />
          <Tab label="Templates" icon={<TemplateIcon />} />
          <Tab label="History" icon={<HistoryIcon />} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 600px', minWidth: 0 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Compose Message
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Target Recipients</InputLabel>
                    <Select
                      value={targetType}
                      onChange={(e) => setTargetType(e.target.value as any)}
                      label="Target Recipients"
                    >
                      <MenuItem value="parents">All Parents</MenuItem>
                      <MenuItem value="students">Specific Students</MenuItem>
                      <MenuItem value="staff">Staff Members</MenuItem>
                      <MenuItem value="class">Specific Class</MenuItem>
                    </Select>
                  </FormControl>

                  {targetType === 'students' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Select Students ({selectedStudents.length} selected)
                      </Typography>
                      <List dense sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider' }}>
                        {students.map((student) => (
                          <ListItem key={student.id} dense>
                            <Checkbox
                              checked={selectedStudents.includes(student.id)}
                              onChange={() => handleStudentSelection(student.id)}
                            />
                            <ListItemText
                              primary={student.name}
                              secondary={`Class: ${student.class_level} | Parent: ${student.parent_email}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {targetType === 'staff' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Select Staff Members ({selectedStaff.length} selected)
                      </Typography>
                      <List dense sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider' }}>
                        {staff.map((member) => (
                          <ListItem key={member.id} dense>
                            <Checkbox
                              checked={selectedStaff.includes(member.id)}
                              onChange={() => handleStaffSelection(member.id)}
                            />
                            <ListItemText
                              primary={member.name}
                              secondary={`${member.role} | ${member.email}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {targetType === 'class' && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Select Class</InputLabel>
                      <Select
                        value={selectedClass}
                        onChange={(e) => setSelectedClass(e.target.value)}
                        label="Select Class"
                      >
                        {classes.map((cls) => (
                          <MenuItem key={cls.class_level} value={cls.class_level}>
                            {cls.class_level} ({cls.student_count} students)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <TextField
                    fullWidth
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Message Content"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enableEmail}
                          onChange={(e) => setEnableEmail(e.target.checked)}
                        />
                      }
                      label="Send via Email"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enableWhatsApp}
                          onChange={(e) => setEnableWhatsApp(e.target.checked)}
                        />
                      }
                      label="Send via WhatsApp"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enableSMS}
                          onChange={(e) => setEnableSMS(e.target.checked)}
                        />
                      }
                      label="Send via SMS"
                    />
                  </Box>

                  {showBulkWarning && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Warning: WhatsApp is enabled but no parent phone numbers are available in the selected recipients.
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleSendMessage}
                    disabled={loading || !content.trim() || (!enableEmail && !enableWhatsApp && !enableSMS)}
                    fullWidth
                  >
                    {loading ? <CircularProgress size={20} /> : `Send to ${getRecipientCount()} Recipients`}
                  </Button>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '0 1 300px', minWidth: 0 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Templates
                  </Typography>
                  <List dense>
                    {templates.slice(0, 5).map((template) => (
                      <ListItem key={template.id} dense>
                        <ListItemText
                          primary={template.name}
                          secondary={template.category}
                        />
                        <ListItemSecondaryAction>
                          <Button
                            size="small"
                            onClick={() => handleUseTemplate(template)}
                          >
                            Use
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 500px', minWidth: 0 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {editingTemplate ? 'Edit Template' : 'Create New Template'}
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Template Name"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Subject"
                    value={templateSubject}
                    onChange={(e) => setTemplateSubject(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Content"
                    value={templateContent}
                    onChange={(e) => setTemplateContent(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={templateType}
                      onChange={(e) => setTemplateType(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                      <MenuItem value="sms">SMS</MenuItem>
                      <MenuItem value="both">Both</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={templateCategory}
                      onChange={(e) => setTemplateCategory(e.target.value)}
                      label="Category"
                    >
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="attendance">Attendance</MenuItem>
                      <MenuItem value="grades">Grades</MenuItem>
                      <MenuItem value="events">Events</MenuItem>
                      <MenuItem value="emergency">Emergency</MenuItem>
                      <MenuItem value="announcement">Announcement</MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
                      disabled={loading || !templateName.trim() || !templateContent.trim()}
                      fullWidth
                    >
                      {loading ? <CircularProgress size={20} /> : (editingTemplate ? 'Update Template' : 'Create Template')}
                    </Button>
                    {editingTemplate && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setEditingTemplate(null);
                          setTemplateName('');
                          setTemplateSubject('');
                          setTemplateContent('');
                          setTemplateType('email');
                          setTemplateCategory('general');
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 500px', minWidth: 0 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Template Library
                  </Typography>
                  <List>
                    {templates.map((template) => (
                      <ListItem key={template.id} divider>
                        <ListItemText
                          primary={template.name}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {template.subject}
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                <Chip label={template.category} size="small" sx={{ mr: 1 }} />
                                <Chip label={template.type} size="small" />
                              </Box>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton onClick={() => handleEditTemplate(template)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteTemplate(template.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Communication History
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Sent At</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {item.type === 'email' ? <EmailIcon /> : item.type === 'whatsapp' ? <WhatsAppIcon /> : <SmsIcon />}
                            <Typography sx={{ ml: 1 }}>{item.type}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{item.recipient}</TableCell>
                        <TableCell>{item.subject}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(item.status)}
                            label={item.status}
                            color={getStatusColor(item.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(item.sent_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => {
                            setSelectedHistory(item);
                            setHistoryDialogOpen(true);
                          }}>
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>

      {/* History Detail Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Message Details</DialogTitle>
        <DialogContent>
          {selectedHistory && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedHistory.subject}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Recipient:</strong> {selectedHistory.recipient}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Type:</strong> {selectedHistory.type}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong> {selectedHistory.status}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Sent:</strong> {new Date(selectedHistory.sent_at).toLocaleString()}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedHistory.content}
              </Typography>
              {selectedHistory.error_message && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>Error:</strong> {selectedHistory.error_message}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolCommunications; 